import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { BaseRequestListComponent } from '../base-request-list/base-request-list.component';
import { CommonGridComponent, GridColumn, GridAction } from '../../../shared/components/common-grid/common-grid.component';
import { RequestManagementService, PaginationInfo } from '../../../shared/services/request-management.service';
import { RequestContextService } from '../../../shared/services/request-context.service';

@Component({
  selector: 'app-draft-requests',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, CommonGridComponent],
  templateUrl: './draft-requests.component.html',
  styleUrls: ['./draft-requests.component.scss']
})
export class DraftRequestListComponent extends BaseRequestListComponent implements OnInit {
  
  // Success message
  successMessage: string = '';

  /**
   * Define columns for draft requests grid
   */
  columns: GridColumn[] = [
    { key: 'number', label: 'Request Number', translationKey: 'requests.number' },
    {
      key: 'module',
      label: 'Module',
      translationKey: 'requests.module',
      type: 'custom',
      customRender: (value: any) => this.translate.instant(`workflow_types.${value}`)
    },
    { key: 'createdDate', label: 'Created Date', translationKey: 'requests.createdDate', type: 'date' },
    { key: 'modifiedDate', label: 'Modified Date', translationKey: 'requests.modifiedDate', type: 'date' }
  ];

  /**
   * Define actions for draft requests
   */
  actions: GridAction[] = [
    {
      id: 'edit',
      label: 'common.edit',
      type: 'edit',
      icon: 'bi-pencil',
      condition: () => true
    },
    {
      id: 'delete',
      label: 'common.delete',
      type: 'delete',
      icon: 'bi-trash',
      condition: () => true
    }
  ];

  constructor(
    private requestManagementService: RequestManagementService,
    private router: Router,
    private translate: TranslateService
  ) {
    super();
  }

  // RequestType-based filtering removed per request

  /**
   * Fetch draft requests - implements abstract method from base class
   */
  override fetchRequests(pageNumber: number, delegationId?: number): void {
    this.loadingSubject$.next(true);
    
    const paginationInfo: PaginationInfo = this.buildPaginationInfo(pageNumber);

    this.requestManagementService
      .getDraftRequests(paginationInfo, delegationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.updateRequests(response);
          this.loadingSubject$.next(false);
        },
        error: (error) => {
          this.handleError(error);
        }
      });
  }

  /**
   * Handle filter change
   */
  // RequestType filter handlers removed

  /**
   * Open the request when its row is clicked
   */
  onRowClick(row: any): void {
    this.editDraft(row);
  }

  /**
   * Handle action triggered from grid
   */
  onTableAction(event: { action: GridAction; row: any }): void {
    switch (event.action.type) {
      case 'edit':
        this.editDraft(event.row);
        break;
      case 'delete':
        this.deleteDraft(event.row);
        break;
    }
  }

  /**
   * Edit draft request - navigate to dynamic-form for editing
   */
  private editDraft(row: any): void {
    this.router.navigate(
      ['/request', row.typeLogicalId],
      { queryParams: { draftId: row.id } }
    );
  }


  /**
   * Delete draft request with confirmation
   */
  private deleteDraft(row: any): void {
    // Show confirmation dialog
    const confirmed = confirm(
      `${this.translate.instant('requests.confirmDeleteDraft')} ${row.number}?`
    );
    
    if (!confirmed) return;
    
    this.loadingSubject$.next(true);
    this.successMessage = '';
    
    this.requestManagementService.deleteDraft(row.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadingSubject$.next(false);
          this.successMessage = this.translate.instant('requests.draftDeleted');
          
          // Auto-hide success message after 3 seconds
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
          
          // Refresh the list
          this.fetchRequests(this.currentPage);
        },
        error: (error) => {
          this.loadingSubject$.next(false);
          this.handleError(error);
          console.error('Error deleting draft:', error);
        }
      });
  }

}
